import { useState } from "react";
import { useStore } from "@tanstack/react-form";
import { useFieldContext } from "@/lib/form/form-context";

interface TextFieldProps {
  label: string;
  type?: "text" | "email" | "url" | "search" | "tel" | "password";
  placeholder?: string;
  autoComplete?: string;
  disabled?: boolean;
  readOnly?: boolean;
  id?: string;
  suffix?: React.ReactNode;
}

export function TextField({
  label,
  type = "text",
  placeholder,
  autoComplete,
  disabled,
  readOnly,
  id,
  suffix,
}: TextFieldProps) {
  const field = useFieldContext<string>();
  const [focused, setFocused] = useState(false);
  const [lastChangedAtSubmissionAttempt, setLastChangedAtSubmissionAttempt] = useState(0);
  const submissionAttempts = useStore(field.form.store, (state) => state.submissionAttempts);
  const fieldId = id ?? field.name;
  const errors = field.state.meta.errors;
  const hasError =
    errors.length > 0 &&
    field.state.meta.isTouched &&
    (!focused || submissionAttempts > lastChangedAtSubmissionAttempt);

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={fieldId} className="text-sm font-medium text-subtle">
        {label}
      </label>
      <div className="relative">
        <input
          id={fieldId}
          name={field.name}
          type={type}
          value={field.state.value}
          placeholder={placeholder}
          autoComplete={autoComplete}
          disabled={disabled}
          readOnly={readOnly}
          aria-disabled={readOnly || undefined}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false);
            field.handleBlur();
          }}
          onChange={(event) => {
            setLastChangedAtSubmissionAttempt(submissionAttempts);
            field.handleChange(event.target.value);
          }}
          className={`
            h-11 w-full rounded-lg border bg-surface text-sm text-heading
            transition-colors duration-150
            placeholder:text-muted
            focus:outline-none focus:ring-2 focus:ring-indigo/40 focus:border-indigo
            disabled:cursor-not-allowed disabled:opacity-50
            aria-disabled:cursor-not-allowed aria-disabled:opacity-50
            ${suffix ? "pr-11 pl-3.5" : "px-3.5"}
            ${hasError ? "border-danger focus:ring-danger/40 focus:border-danger" : "border-border hover:border-border-light"}
          `}
          aria-invalid={hasError || undefined}
          aria-describedby={hasError ? `${fieldId}-error` : undefined}
        />
        {suffix}
      </div>
      {hasError && (
        <p id={`${fieldId}-error`} role="alert" className="text-xs text-danger">
          {[...new Set(errors.map((e) => (typeof e === "string" ? e : e.message)))].join(", ")}
        </p>
      )}
    </div>
  );
}
